import React, { useState, useRef, ReactNode } from "react";
import { Drawer, Box } from "@mui/material";

interface BaseDrawerProps {
  open: boolean;
  handleClose: () => void;
  height?: string;
  children: ReactNode;
  background?: string;
}

const BaseDrawer: React.FC<BaseDrawerProps> = ({ 
  open, 
  handleClose, 
  height = "85vh",
  children,
  background = "linear-gradient(135deg, #f8fafa 0%, #e9f0ee 100%)"
}) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const isScrollable = target.closest('[data-scrollable]');
    if (!isScrollable) {
      setDragStartY(e.touches[0].clientY);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - dragStartY;
    
    if (deltaY > 0) {
      setDragCurrentY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    if (dragCurrentY > 100) {
      handleClose();
    }
    
    setIsDragging(false);
    setDragCurrentY(0);
    setDragStartY(0);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          height,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          margin: 0,
          padding: 0,
          background,
          transform: isDragging ? `translateY(${dragCurrentY}px)` : "translateY(0px)",
          transition: isDragging ? "none" : "transform 0.3s ease-out",
        },
      }}
    >
      <Box 
        ref={drawerRef}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Box
          sx={{
            width: "100%",
            py: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "grab",
            flexShrink: 0,
            "&:active": {
              "& > div": {
                bgcolor: "rgba(83, 105, 109, 0.6)",
              },
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: "rgba(83, 105, 109, 0.4)",
              borderRadius: 2,
            }}
          />
        </Box>
        {children}
      </Box>
    </Drawer>
  );
};

export default BaseDrawer;